import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  AvatarBorderType,
  AvatarComponent
} from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { AvatarsGroupComponent } from '@app/shared/components/group-avatars/proffeo-group-avatars.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { GroupsDataService } from '@app/shared/services/groups-data.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { GroupRole } from '@app/shared/types/enums/group-role.enum';
import { Company } from '@app/shared/types/models/company/company.model';
import { GroupUser } from '@app/shared/types/models/group/group-user.model';
import { Group } from '@app/shared/types/models/group/group.model';
import { User } from '@app/shared/types/models/user/user.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-company-groups-list-item',
  imports: [MatIconModule, AvatarsGroupComponent, AvatarComponent, MatMenuModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './company-groups-list-item.component.html'
})
export class CompanyGroupsListItemComponent {
  private readonly groupsService: GroupsDataService = inject(GroupsDataService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;
  private lastChildCount = -1;

  protected readonly AvatarSize = AvatarSize;
  protected readonly AvatarBorderType = AvatarBorderType;
  protected readonly groupChildren: Signal<Signal<Group>[]> = computed(() =>
    this.groupsService.getGroupChildren(this.companyGroup().id)()
  );
  protected readonly hasChildGroups: Signal<boolean> = computed(
    () => (this.groupChildren()?.length ?? 0) > 0 || (this.companyGroup()?.childrenCount ?? 0) > 0
  );
  protected readonly expanded: WritableSignal<boolean> = signal(false);
  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly calculatedLeftPadding: Signal<number> = computed(() =>
    this.isMobile() ? 8 : (this.level() ?? 1) * 8 + 24
  );
  protected readonly groupUsers: Signal<User[]> = computed(() =>
    this.companyGroup()
      ?.users()
      ?.filter(user => user.role === GroupRole.NONE)
      .map(user => user.applicationUser())
  );
  protected readonly groupAdmin: Signal<GroupUser> = computed(() =>
    this.companyGroup()
      ?.users()
      ?.find(groupUser => groupUser.role === GroupRole.ADMIN)
  );

  public readonly companyGroup: InputSignal<Group | undefined> = input();
  public readonly level: InputSignal<number> = input(1);

  public constructor() {
    effect(() => {
      const count = this.groupChildren()?.length ?? 0;
      if (this.lastChildCount < 0) {
        this.lastChildCount = count;
        return;
      }
      if (count > this.lastChildCount) {
        untracked(() => this.expanded.set(true));
      }
      this.lastChildCount = count;
    });
  }

  protected toggleExpanded(): void {
    this.expanded.update(expanded => !expanded);
  }

  protected openCreateCompanyGroupDialog(groupParentId: string): void {
    this.dialogService.openCreateCompanyGroupDialog(groupParentId);
  }

  protected openEditCompanyGroupNameDialog(group: Group): void {
    this.dialogService.openEditCompanyGroupNameDialog(group);
  }

  protected selectUsers(preselectedUsers: GroupUser[], lockedUser: User): void {
    const preselectedMappedUsers = preselectedUsers.map(user => user.applicationUser());
    this.dialogService.openSelectUsersToCompanyGroupDialog(preselectedMappedUsers, lockedUser, this.companyGroup());
  }

  protected selectGroupAdmin(preselectedUser: User): void {
    this.dialogService.opensSelectGroupAdminDialog(preselectedUser, this.companyGroup());
  }

  protected openGroupDeleteConfirmationDialog(group: Group): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('company-groups.modals.delete-modal-title'),
        desc: this.translateService.instant('company-groups.modals.delete-modal-description', {
          value: group.name
        })
      })
      .afterClosed()
      .subscribe(decision => {
        if (decision) {
          this.deleteCompanyGroup(group);
        }
      });
  }

  private deleteCompanyGroup(group: Group): void {
    this.groupsService.deleteGroup(this.activeCompany()?.id, group);
  }
}
